import "./simple-pdf-tool.css";
import "./pdf-overlay.css";
import { openBrowserPdf, pdfErrorMessage, renderPageIntoCanvas } from "./pdf-browser-core.js";
import { createOperationResult } from "./operation-result.js";
import { addPageNumbers, addTextWatermark, calculatePlacement, formatPageNumber, numberedFileName, validateNumberSettings, validateWatermarkSettings, watermarkedFileName } from "./pdf-overlay-core.js";

const root = document.querySelector("[data-pdf-overlay]");

if (root) {
  const mode = root.dataset.overlayMode;
  const ids = ["overlayStatus", "overlayUploadPanel", "overlayDropZone", "chooseOverlayPdf", "overlayFileInput", "overlayWorkspace", "overlayFileName", "overlayPageCount", "overlayPreviewPage", "replaceOverlayPdf", "overlayPreviewCanvas", "applyPdfOverlay", "overlayProgress"];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = { bytes: null, document: null, fileName: "document.pdf", pageCount: 0, busy: false, previewQueued: false, previewVersion: 0 };
  const byId = id => document.getElementById(id);
  const operationResult = createOperationResult(root, elements.overlayWorkspace);

  function showStatus(message = "") { elements.overlayStatus.textContent = message; elements.overlayStatus.hidden = !message; }
  function settings() {
    return mode === "number" ? {
      format: byId("numberFormat").value, position: byId("numberPosition").value, firstPage: byId("numberFirstPage").value,
      start: byId("numberStart").value, size: byId("numberSize").value, color: byId("numberColor").value
    } : {
      text: byId("watermarkText").value, color: byId("watermarkColor").value, size: byId("watermarkSize").value,
      opacity: Number(byId("watermarkOpacity").value) / 100, angle: byId("watermarkAngle").value, position: byId("watermarkPosition").value,
      apply: byId("watermarkApply").value, from: byId("watermarkFrom").value, to: byId("watermarkTo").value
    };
  }
  function updateControls() { elements.replaceOverlayPdf.disabled = state.busy; elements.applyPdfOverlay.disabled = state.busy || !state.document; }

  async function loadFile(file) {
    if (state.busy) return;
    showStatus(); state.busy = true; updateControls(); let loaded = null;
    try {
      loaded = await openBrowserPdf(file);
      if (state.document) await state.document.destroy();
      state.bytes = loaded.bytes; state.document = loaded.document; state.fileName = loaded.fileName; state.pageCount = loaded.pageCount; loaded = null;
      elements.overlayFileName.textContent = state.fileName; elements.overlayPageCount.textContent = String(state.pageCount);
      if (mode === "number") byId("numberFirstPage").max = String(state.pageCount);
      else { byId("watermarkFrom").max = String(state.pageCount); byId("watermarkTo").max = String(state.pageCount); byId("watermarkTo").value = String(state.pageCount); }
      elements.overlayUploadPanel.hidden = true; elements.overlayWorkspace.hidden = false; await renderPreview();
    } catch (error) { if (loaded?.document) await loaded.document.destroy(); showStatus(pdfErrorMessage(error)); }
    finally { state.busy = false; elements.overlayFileInput.value = ""; updateControls(); }
  }

  function previewPageNumber() {
    const value = mode === "number" ? Number(byId("numberFirstPage").value) : byId("watermarkApply").value === "range" ? Number(byId("watermarkFrom").value) : 1;
    return Math.max(1, Math.min(state.pageCount, value || 1));
  }
  async function renderPreview() {
    const version = ++state.previewVersion;
    state.previewQueued = false; if (!state.document) return;
    const pageNumber = previewPageNumber(); elements.overlayPreviewPage.textContent = String(pageNumber);
    const page = await state.document.getPage(pageNumber);
    try {
      const natural = page.getViewport({ scale: 1 }); const scale = Math.min(560 / natural.width, 580 / natural.height, 1.25);
      await renderPageIntoCanvas(page, elements.overlayPreviewCanvas, { scale });
      if (version === state.previewVersion) drawPreviewOverlay(scale, pageNumber);
    } finally { page.cleanup(); }
  }
  function drawPreviewOverlay(scale, pageNumber) {
    const values = settings(); const canvas = elements.overlayPreviewCanvas; const context = canvas.getContext("2d");
    const size = Number(values.size) * scale;
    const text = mode === "number" ? formatPageNumber(Number(values.start) + pageNumber - Number(values.firstPage), Number(values.start) + state.pageCount - Number(values.firstPage), values.format) : values.text;
    context.save(); context.font = `${mode === "watermark" ? "700 " : ""}${size}px sans-serif`; context.fillStyle = values.color; context.globalAlpha = mode === "watermark" ? values.opacity : 1;
    const metrics = context.measureText(text); const pdfPosition = calculatePlacement(canvas.width, canvas.height, metrics.width, size, values.position, 24 * scale);
    const x = pdfPosition.x; const y = canvas.height - pdfPosition.y - size;
    context.translate(x + metrics.width / 2, y + size / 2); context.rotate((mode === "watermark" ? Number(values.angle) : 0) * Math.PI / 180); context.fillText(text, -metrics.width / 2, size / 2); context.restore();
  }
  function queuePreview() {
    state.previewVersion += 1;
    if (!state.previewQueued) { state.previewQueued = true; requestAnimationFrame(renderPreview); }
  }

  async function applyOverlay() {
    const values = settings(); const error = mode === "number" ? validateNumberSettings(values, state.pageCount) : validateWatermarkSettings(values, state.pageCount);
    if (error) { showStatus(error); return; }
    showStatus(); state.busy = true; elements.overlayProgress.hidden = false; updateControls();
    try {
      const progress = (current, total) => { elements.overlayProgress.textContent = `Processing page ${current} of ${total}`; };
      const bytes = mode === "number" ? await addPageNumbers(state.bytes, values, progress) : await addTextWatermark(state.bytes, values, progress);
      operationResult.show({
        blob: new Blob([bytes], { type: "application/pdf" }),
        fileName: mode === "number" ? numberedFileName(state.fileName) : watermarkedFileName(state.fileName),
        message: mode === "number" ? "Page numbers were added successfully." : "Your watermark was added successfully.",
        details: [`${state.pageCount} ${state.pageCount === 1 ? "page" : "pages"}`]
      });
    } catch (error) { showStatus(error?.message || "smallpdf.app could not update this PDF."); }
    finally { state.busy = false; elements.overlayProgress.hidden = true; updateControls(); }
  }

  elements.chooseOverlayPdf.addEventListener("click", event => { event.stopPropagation(); elements.overlayFileInput.click(); });
  elements.replaceOverlayPdf.addEventListener("click", () => elements.overlayFileInput.click());
  elements.overlayFileInput.addEventListener("change", () => loadFile(elements.overlayFileInput.files?.[0]));
  elements.applyPdfOverlay.addEventListener("click", applyOverlay);
  root.querySelectorAll("input, select").forEach(control => control.addEventListener("input", () => {
    if (control.id === "watermarkApply") byId("watermarkRange").hidden = control.value !== "range";
    if (control.id === "watermarkOpacity") byId("watermarkOpacityValue").textContent = `${control.value}%`;
    if (control.id === "watermarkAngle") byId("watermarkAngleValue").textContent = `${control.value}°`;
    queuePreview();
  }));
  elements.overlayDropZone.addEventListener("click", event => { if (!event.target.closest("button")) elements.overlayFileInput.click(); });
  elements.overlayDropZone.addEventListener("keydown", event => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); elements.overlayFileInput.click(); } });
  elements.overlayDropZone.addEventListener("dragover", event => { event.preventDefault(); elements.overlayDropZone.classList.add("is-dragging"); });
  elements.overlayDropZone.addEventListener("dragleave", () => elements.overlayDropZone.classList.remove("is-dragging"));
  elements.overlayDropZone.addEventListener("drop", event => { event.preventDefault(); elements.overlayDropZone.classList.remove("is-dragging"); loadFile(event.dataTransfer.files?.[0]); });
  window.addEventListener("beforeunload", () => { operationResult.dispose(); if (state.document) state.document.destroy(); });
}
